import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const checkAuth = useCallback(async () => {
        try {
            const res = await api.get("/auth/me");
            setUser(res.data);
        } catch (_e) {
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // CRITICAL: If returning from OAuth callback, skip the /me check.
        // AuthCallback will exchange the session_id and establish the session first.
        if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        checkAuth();
    }, [checkAuth]);

    const login = useCallback(async (email, password) => {
        const res = await api.post("/auth/login", { email, password });
        localStorage.setItem("boxeb2b_jwt", res.data.token);
        setUser(res.data.user);
        return res.data.user;
    }, []);

    const register = useCallback(async (email, password, name) => {
        const res = await api.post("/auth/register", { email, password, name });
        localStorage.setItem("boxeb2b_jwt", res.data.token);
        setUser(res.data.user);
        return res.data.user;
    }, []);

    const logout = useCallback(async () => {
        try { await api.post("/auth/logout"); } catch (_e) { /* ignore */ }
        localStorage.removeItem("boxeb2b_jwt");
        setUser(null);
    }, []);

    const value = useMemo(() => ({ user, loading, login, register, logout, refresh: checkAuth, setUser }),
        [user, loading, login, register, logout, checkAuth]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}
